import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

interface Testimonial {
  text: string;
  name: string;
  role: string;
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
}

export default function TestimonialCarousel({ testimonials }: TestimonialCarouselProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const goTo = (index: number) => {
    setCurrent(index < 0 ? testimonials.length - 1 : index % testimonials.length);
  };

  return (
    <div className="relative max-w-3xl mx-auto">
      <div className="min-h-[240px] flex items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.35 }}
            className="bg-white rounded-2xl p-8 md:p-10 shadow-lg shadow-gray-100/50 ring-1 ring-gray-100 text-center w-full relative"
          >
            <div className="absolute top-6 start-8 opacity-10">
              <Quote className="w-12 h-12 text-cyan-600" aria-hidden="true" />
            </div>
            <p className="text-gray-700 text-lg md:text-xl leading-relaxed mb-6 relative z-10">
              &ldquo;{testimonials[current].text}&rdquo;
            </p>
            <div>
              <p className="font-bold text-gray-900">{testimonials[current].name}</p>
              <p className="text-gray-400 text-sm mt-0.5">{testimonials[current].role}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-center gap-4 mt-6">
        <button
          onClick={() => goTo(current - 1)}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Previous testimonial"
        >
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>

        <div className="flex gap-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Testimonial ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === current ? 'bg-cyan-600 w-8' : 'bg-gray-200 hover:bg-gray-300 w-2'
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => goTo(current + 1)}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Next testimonial"
        >
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
      </div>
    </div>
  );
}
